import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type {
  CallStatsChannelReasonRow,
  CallStatsEndedReasonRow,
} from "@/services/callStatsService";
import ChannelToggle, { type ChannelFilter } from "./ChannelToggle";
import DonutBreakdown from "./DonutBreakdown";

function humanise(reason: string): string {
  if (!reason) return "Not recorded";
  const text = reason.replace(/[-_]+/g, " ").trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

interface EndedReasonChartProps {
  rows: CallStatsEndedReasonRow[];
  rowsByChannel?: CallStatsChannelReasonRow[];
}

const EndedReasonChart = ({ rows, rowsByChannel }: EndedReasonChartProps) => {
  const [channel, setChannel] = useState<ChannelFilter>("all");

  const items = useMemo(() => {
    const visible =
      channel === "all" || !rowsByChannel
        ? rows
        : rowsByChannel
            .filter((row) => row.channel === channel)
            .sort((a, b) => b.calls - a.calls);
    return visible.map((row) => ({ name: humanise(row.reason), value: row.calls }));
  }, [rows, rowsByChannel, channel]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Why calls ended</CardTitle>
        <ChannelToggle
          value={channel}
          onChange={setChannel}
          disabled={!rowsByChannel}
        />
      </CardHeader>
      <CardContent>
        <DonutBreakdown key={channel} items={items} unitLabel="calls" />
      </CardContent>
    </Card>
  );
};

export default EndedReasonChart;
